import { UserStoreFactory } from "@/stores/user";
import { GenericController } from "@/stores/controller";
import { fetchApiKey } from "@/stores/util.dom";
import type { RevBooster } from "@/utils/revBooster";

const apikey = fetchApiKey();
const useUserStore = UserStoreFactory(apikey + "_user");

let timerStartedAt: number | undefined;
const scheduledIds = new Set<string>();
let queuedBoosters: RevBooster[] = [];
let activeBoosterId: string | undefined;
let timers: ReturnType<typeof setTimeout>[] = [];
let queueFlushTimer: ReturnType<typeof setTimeout> | undefined;

const isHomePage = () => window.location.pathname.includes("/page/home");

const hasBlockingModal = () => {
  if (!GenericController.HasCurrentController()) return true;

  const controller = GenericController.CurrentController();
  return (
    Object.values(controller.modalStates).some(Boolean) ||
    controller.errorModal ||
    controller.iFrame ||
    controller.loadingModal ||
    controller.freeModal
  );
};

const isAvailable = (boosterId: string) =>
  useUserStore().availableIntervalRevBooster.some(
    (booster: RevBooster) => booster._id === boosterId,
  );

const enqueue = (booster: RevBooster) => {
  if (!booster._id || !isAvailable(booster._id)) return;
  if (
    booster._id === activeBoosterId ||
    queuedBoosters.some((queued) => queued._id === booster._id)
  ) {
    return;
  }
  queuedBoosters.push(booster);
};

const popNextAvailable = () => {
  while (queuedBoosters.length) {
    const booster = queuedBoosters.shift();
    if (booster?._id) {
      const current = useUserStore().availableIntervalRevBooster.find(
        (entry: RevBooster) => entry._id === booster._id,
      );
      if (current) return current;
    }
  }
};

/** Opens at most one due interval promotion at a time. */
export const showNextQueuedRevBooster = () => {
  if (!isHomePage() || hasBlockingModal()) return;

  const booster = popNextAvailable();
  if (!booster?._id) return;

  activeBoosterId = booster._id;
  useUserStore().$patch({ selectedRevBooster: booster });
  GenericController.CurrentController().toggleFreeModal(true);
};

/**
 * Schedules every interval promotion relative to the first Home-page render.
 * If several are due while another dialog is open, they wait in FIFO order.
 */
export const startRevBoosterTimer = () => {
  timerStartedAt ??= Date.now();

  useUserStore().availableIntervalRevBooster.forEach((booster: RevBooster) => {
    if (!booster._id || scheduledIds.has(booster._id)) return;

    scheduledIds.add(booster._id);
    const durationMs =
      Math.max(0, Number(booster.intervalDuration) || 0) * 1000;
    const elapsedMs = Date.now() - timerStartedAt!;
    const delayMs = Math.max(0, durationMs - elapsedMs);
    const timer = setTimeout(() => {
      enqueue(booster);
      showNextQueuedRevBooster();
    }, delayMs);
    timers.push(timer);
  });

  showNextQueuedRevBooster();
  return timers;
};

/** Pauses outstanding callbacks without restarting the page-load schedule. */
export const stopRevBoosterTimer = () => {
  timers.forEach(clearTimeout);
  timers = [];
  scheduledIds.clear();
};

/**
 * Closes the current promotion and advances an already due queued promotion
 * after the modal leave transition completes.
 */
export const dismissRevBoosterModal = () => {
  activeBoosterId = undefined;
  if (GenericController.HasCurrentController()) {
    GenericController.CurrentController().toggleFreeModal(false);
  }

  if (queueFlushTimer) clearTimeout(queueFlushTimer);
  queueFlushTimer = setTimeout(showNextQueuedRevBooster, 180);
};

/** Clears queue state when a new app/page lifetime begins. */
export const resetRevBoosterQueue = () => {
  stopRevBoosterTimer();
  if (queueFlushTimer) clearTimeout(queueFlushTimer);
  queueFlushTimer = undefined;
  queuedBoosters = [];
  activeBoosterId = undefined;
  timerStartedAt = undefined;
};

export const refreshToken = async () => useUserStore().getToken(apikey!);
